import {
    createContext,
    ReactElement,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'

import { useSelector } from 'react-redux'

import { CerebroResourceResponse } from 'api/Cerebro/types'
import { getOrganizationAccountStatus } from 'api/Cerebro/unscoped/organization'
import { CustomerModuleKey } from 'const/customerModules'
import { useCurrentOrganizationGroup } from 'hooks/redux/orgs'
import {
    CustomerModuleInfo,
    useCustomerModules,
} from 'hooks/useCustomerModules'
import { selectCurrentOrganization } from 'selectors/orgs'
import { OrgAccountStatus, Organization, RootReduxState } from 'types'

import useCerebroApiRequest from '../api/useCerebroApiRequest'

interface OrgContext {
    orgId?: string
    orgGroupId?: string
    orgAccountStatus?: OrgAccountStatus & {
        // eventually this could be completely calculated on the server
        customerModules: Record<CustomerModuleKey, CustomerModuleInfo>
    }
    org?: Organization
}

const OrgContextInstance = createContext<OrgContext>({
    orgId: undefined,
    orgGroupId: undefined,
    org: undefined,
})

export const useOrgContext = (): OrgContext => useContext(OrgContextInstance)

/** Scope from outside redux, for routes rendering as someone other than the session user. */
export interface OrgContextOverride {
    org?: Organization
    orgAccountStatus?: OrgAccountStatus
    orgId?: string
    orgGroupId?: string
}

export function OrgContextProvider(props: {
    children: ReactNode
    override?: OrgContextOverride
}): ReactElement {
    const { children, override } = props
    const [orgAccountStatus, setOrgAccountStatus] = useState<OrgAccountStatus>()
    const makeCerebroApiRequest = useCerebroApiRequest()
    const currentOrg = useSelector<RootReduxState, Organization | undefined>(
        (state) => selectCurrentOrganization(state)
    )
    const currentOrganizationGroup = useCurrentOrganizationGroup()
    const customerModuleInfo = useCustomerModules()

    const orgId = override ? override.orgId : currentOrg?.id
    const orgGroupId = override
        ? override.orgGroupId
        : currentOrganizationGroup?.id

    useEffect(() => {
        async function fetchOrgAccountStatus(): Promise<void> {
            if (orgId) {
                await makeCerebroApiRequest({
                    request: getOrganizationAccountStatus(orgId),
                    onRequestSuccess: (
                        response: CerebroResourceResponse<OrgAccountStatus>
                    ) => {
                        setOrgAccountStatus(response.data)
                    },
                })
            }
        }
        if (orgId && !override) {
            setOrgAccountStatus(undefined)
            fetchOrgAccountStatus()
        }
    }, [orgId, makeCerebroApiRequest, override])

    const orgContext = useMemo(() => {
        const resolvedOrg = override ? override.org : currentOrg
        const resolvedAccountStatus = override
            ? override.orgAccountStatus
            : orgAccountStatus

        return {
            org: resolvedOrg,
            orgId,
            orgGroupId,
            orgAccountStatus: resolvedAccountStatus
                ? {
                      ...resolvedAccountStatus,
                      customerModules: customerModuleInfo,
                  }
                : undefined,
        }
    }, [
        override,
        currentOrg,
        orgId,
        orgGroupId,
        orgAccountStatus,
        customerModuleInfo,
    ])

    return (
        <OrgContextInstance.Provider value={orgContext}>
            {children}
        </OrgContextInstance.Provider>
    )
}
